/**
 * API client helpers — thin wrappers around the Next.js /api routes.
 * Use these in client components instead of calling Supabase directly.
 *
 * All functions return { data, error } to mirror the Supabase client pattern.
 */

const BASE = '/api';

async function apiFetch<T = any>(
  path: string,
  options?: RequestInit
): Promise<{ data: T | null; error: string | null }> {
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    const json = await res.json();
    if (!res.ok) {
      return { data: null, error: json.error ?? `HTTP ${res.status}` };
    }
    return { data: json.data ?? json, error: null };
  } catch (err: any) {
    return { data: null, error: err.message ?? 'Network error' };
  }
}

// ─── Scam Alerts ─────────────────────────────────────────────────────────────

export const scamAlertsApi = {
  list: (params?: { severity?: string; search?: string }) => {
    const qs = new URLSearchParams(params as any).toString();
    return apiFetch(`/scam-alerts${qs ? `?${qs}` : ''}`);
  },
  submit: (body: Record<string, any>) =>
    apiFetch('/scam-alerts', { method: 'POST', body: JSON.stringify(body) }),
};

// ─── Ads ─────────────────────────────────────────────────────────────────────

export const adsApi = {
  list: (params?: { placement?: string; status?: string }) => {
    const qs = new URLSearchParams(params as any).toString();
    return apiFetch(`/ads${qs ? `?${qs}` : ''}`);
  },
  myAds: () => apiFetch('/ads/my-ads'),
  submit: (body: Record<string, any>) =>
    apiFetch('/ads', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { ad_status: string }) =>
    apiFetch(`/ads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch(`/ads/${id}`, { method: 'DELETE' }),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export const notificationsApi = {
  list: (params?: { type?: string; limit?: number }) => {
    const qs = new URLSearchParams(params as any).toString();
    return apiFetch(`/notifications${qs ? `?${qs}` : ''}`);
  },
  markAllRead: () => apiFetch('/notifications', { method: 'PATCH' }),
  deleteAll: () => apiFetch('/notifications', { method: 'DELETE' }),
  markRead: (id: string) => apiFetch(`/notifications/${id}`, { method: 'PATCH' }),
  delete: (id: string) => apiFetch(`/notifications/${id}`, { method: 'DELETE' }),
};

// ─── Profile ─────────────────────────────────────────────────────────────────

export const profileApi = {
  me: () => apiFetch('/profile'),
  get: (id: string) => apiFetch(`/profile/${id}`),
  update: (body: Record<string, any>) =>
    apiFetch('/profile', { method: 'PATCH', body: JSON.stringify(body) }),
};

// ─── Blogs ───────────────────────────────────────────────────────────────────

export const blogsApi = {
  list: (params?: { tag?: string; search?: string; tab?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams(Object.entries(params ?? {}).filter(([, value]) => value !== undefined && value !== null && value !== '').map(([key, value]) => [key, String(value)])).toString();
    return apiFetch(`/blogs${qs ? `?${qs}` : ''}`);
  },
  get: (slug: string) => apiFetch(`/blogs/${slug}`),
  myBlogs: () => apiFetch('/blogs/my-blogs'),
  create: (body: Record<string, any>) =>
    apiFetch('/blogs', { method: 'POST', body: JSON.stringify(body) }),
  update: (slug: string, body: Record<string, any>) =>
    apiFetch(`/blogs/${slug}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (slug: string) => apiFetch(`/blogs/${slug}`, { method: 'DELETE' }),
};

// ─── Community ───────────────────────────────────────────────────────────────

export const communityApi = {
  posts: {
    list: (params?: { tab?: string; tag?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return apiFetch(`/community/posts${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => apiFetch(`/community/posts/${id}`),
    create: (body: Record<string, any>) =>
      apiFetch('/community/posts', { method: 'POST', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch(`/community/posts/${id}`, { method: 'DELETE' }),
    like: (id: string) => apiFetch(`/community/posts/${id}/like`, { method: 'POST' }),
  },
  groups: {
    list: (params?: { category?: string; search?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return apiFetch(`/community/groups${qs ? `?${qs}` : ''}`);
    },
    create: (body: Record<string, any>) =>
      apiFetch('/community/groups', { method: 'POST', body: JSON.stringify(body) }),
  },
};

// ─── Projects ────────────────────────────────────────────────────────────────

export const projectsApi = {
  list: (params?: { category?: string; status?: string; search?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams(params as any).toString();
    return apiFetch(`/projects${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => apiFetch(`/projects/${id}`),
  mySubmissions: () => apiFetch('/projects/submit'),
  submit: (body: Record<string, any>) =>
    apiFetch('/projects/submit', { method: 'POST', body: JSON.stringify(body) }),
  reviews: {
    list: (projectId: string) => apiFetch(`/projects/${projectId}/reviews`),
    submit: (projectId: string, body: { rating: number; review_text?: string }) =>
      apiFetch(`/projects/${projectId}/reviews`, { method: 'POST', body: JSON.stringify(body) }),
  },
};

// ─── Dashboard ───────────────────────────────────────────────────────────────

export const dashboardApi = {
  summary: () => apiFetch('/dashboard'),
};

// ─── Friends ─────────────────────────────────────────────────────────────────

export const friendsApi = {
  list: (status?: string) => apiFetch(`/friends${status ? `?status=${status}` : ''}`),
  sendRequest: (receiver_id: string) =>
    apiFetch('/friends', { method: 'POST', body: JSON.stringify({ receiver_id }) }),
  respond: (id: string, status: 'accepted' | 'declined') =>
    apiFetch(`/friends/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

// ─── Messages ────────────────────────────────────────────────────────────────

export const messagesApi = {
  list: () => apiFetch('/messages'),
  send: (receiver_id: string, content: string) =>
    apiFetch('/messages', { method: 'POST', body: JSON.stringify({ receiver_id, content }) }),
};

// ─── Learning Hub ────────────────────────────────────────────────────────────

export const learningHubApi = {
  list: (params?: { category?: string; search?: string; level?: string }) => {
    const qs = new URLSearchParams(params as any).toString();
    return apiFetch(`/learning-hub${qs ? `?${qs}` : ''}`);
  },
};

// ─── Watchlist ───────────────────────────────────────────────────────────────

export const watchlistApi = {
  list: () => apiFetch('/watchlist'),
  add: (project_id: string) =>
    apiFetch('/watchlist', { method: 'POST', body: JSON.stringify({ project_id }) }),
  remove: (project_id: string) => apiFetch(`/watchlist?project_id=${project_id}`, { method: 'DELETE' }),
};

// ─── Admin ───────────────────────────────────────────────────────────────────

export const adminApi = {
  ads: {
    list: (status?: string) => apiFetch(`/admin/ads${status ? `?status=${status}` : ''}`),
    update: (id: string, body: { ad_status: string; admin_note?: string }) =>
      apiFetch(`/admin/ads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  projects: {
    list: (status?: string) => apiFetch(`/admin/projects${status ? `?status=${status}` : ''}`),
    update: (id: string, body: { status: string; admin_feedback?: string }) =>
      apiFetch(`/admin/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  scamReports: {
    list: (status?: string) => apiFetch(`/admin/scam-reports${status ? `?status=${status}` : ''}`),
    update: (id: string, body: { report_status?: string; verified?: boolean; severity?: string }) =>
      apiFetch(`/admin/scam-reports/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
};
